/* Billing status rendering helpers (stabilization round, workstream H).

   The backend (provider_billing.assess) keeps ten things SEPARATE — integration
   health, data availability, manual vs automatic data, manual-data freshness,
   plan/budget, spend, month-end estimate, genuinely remaining balance,
   promotional credits and risk — and folds them into one of seven UI states.
   This module is the single place the UI turns that into words/colours, so the
   Billing page and the Home tile can never disagree, and "Unknown" can never be
   mistaken for "Failed". Pure functions only (unit-tested). */

export const STATE_TONE = {
  healthy: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  warning: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  critical: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]",
  unknown_no_budget: "bg-muted text-muted-foreground",
  unknown_no_data: "bg-muted text-muted-foreground",
  integration_error: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
  manual_stale: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
};

export const STATE_LABEL = {
  healthy: "Healthy",
  warning: "Warning",
  critical: "Critical",
  unknown_no_budget: "Unknown - no budget set",
  unknown_no_data: "Unknown - no data",
  integration_error: "Integration error",
  manual_stale: "Manual data stale",
};

export const INTEGRATION_LABEL = {
  ok: "Working",
  error: "Error",
  missing_scope: "Missing API permission",
  not_configured: "Not configured",
  manual_mode: "Manual mode (no API calls)",
  not_applicable: "No billing API - manual tracking",
  never_checked: "Not checked yet",
  disabled: "Tracking disabled",
};

export const SOURCE_LABEL = { automatic: "Automatic (provider API)", manual: "Manual (administrator-entered)", none: "None yet" };

export const fmtMoney = (val, currency) => (val == null || val === ""
  ? "—"
  : `${(currency || "").toUpperCase()} ${Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 })}`.trim());

/* The states that need a human to act (used for the Home "needs attention" count).
   unknown_* is deliberately NOT included: an unset budget is not an incident. */
export const NEEDS_ATTENTION = ["warning", "critical", "integration_error", "manual_stale"];

/* Falls back to the legacy single-word status for an older API payload. */
export const stateOf = (p) => {
  if (p && p.state && STATE_LABEL[p.state]) return p.state;
  const legacy = { healthy: "healthy", due_soon: "warning", low_balance: "warning", critical: "critical", payment_issue: "critical" };
  return legacy[p && p.status] || "unknown_no_data";
};

/* One-line detail for the Home tile: what we actually know, never a bare "Unknown". */
export const homeTileDetail = (p) => {
  const st = stateOf(p);
  const cur = p.currency;
  if (st === "unknown_no_budget") {
    return p.current_spend != null
      ? `${fmtMoney(p.current_spend, cur)} spent · no budget set`
      : "no budget set";
  }
  if ((st === "healthy" || st === "warning" || st === "critical") && p.budget_configured && p.current_spend != null) {
    const pct = p.budget_used_percent != null ? ` (${Math.round(p.budget_used_percent)}%)` : "";
    return `${fmtMoney(p.current_spend, cur)} of ${fmtMoney(p.monthly_budget, cur)}${pct}`;
  }
  if (st === "warning" && p.current_usage != null && p.usage_limit) {
    return `${Math.max(0, Math.round(100 * (1 - p.current_usage / p.usage_limit)))}% remaining`;
  }
  return null;
};

export const homeTileLabel = (p) => STATE_LABEL[stateOf(p)];

export const freshnessText = (manual) => {
  if (!manual) return null;
  if (manual.freshness === "stale") return `Stale - ${manual.stale_reason || "not updated recently"}`;
  if (manual.freshness === "fresh") return `Fresh (limit ${manual.stale_after_days} day${manual.stale_after_days === 1 ? "" : "s"})`;
  if (manual.freshness === "unknown_age") return "Update time unknown";
  return null;
};

/* "Only usage + budget are available" style explanation, from the provider's declared capabilities. */
export const coverageText = (assessment) => {
  if (!assessment) return null;
  const cap = assessment.capabilities || {};
  const parts = [];
  if (cap.spend) parts.push("spend/usage");
  if (cap.promotional_credits) parts.push("unused promotional credits");
  if (cap.remaining_balance) parts.push("remaining balance/quota");
  if (cap.plan_and_renewal) parts.push("plan and renewal date");
  return parts.length ? `The provider API supplies: ${parts.join(", ")}.` : "The provider API supplies no billing data - track it manually.";
};
